import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import DrawIcon from '@mui/icons-material/Draw';
import CloseIcon from '@mui/icons-material/Close';
import PdfFitPreview from '../components/common/PdfFitPreview';
import PageToastStack from '../components/common/PageToastStack';
import usePageToast from '../hooks/usePageToast';
import {
  createInvoiceTemplate,
  deleteInvoiceTemplate,
  fetchInvoiceTemplatePreviewBlob,
  getInvoiceSetup,
  updateInvoiceSetup,
} from '../services/api';
import {
  getConfigurationCompactCardSx,
  getConfigurationCompactSectionTitleSx,
  getConfigurationNeutralOutlinedButtonSx,
  getConfigurationTextFieldSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import { normalizeInvoiceStart, previewInvoiceNumber } from '../utils/invoiceNumberSequence';

const ACCENT = '#4ce1b6';

function normalizeHsn(value, fallback = '') {
  const trimmed = String(value ?? '').trim();
  return trimmed || fallback;
}

const LAYOUT_SWATCH = {
  classic: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
  classic_bordered: 'linear-gradient(180deg, #e8e8e8 0%, #ffffff 50%, #333333 100%)',
  luxury: 'linear-gradient(135deg, #0c1a3a 0%, #c9a227 100%)',
  mint_premium: 'linear-gradient(135deg, #0d2b24 0%, #4ce1b6 100%)',
  mint_premium_ii: 'linear-gradient(135deg, #4ce1b6 0%, #8ef0d4 50%, #2ec99a 100%)',
  mint_premium_iii: 'linear-gradient(180deg, #ffffff 0%, #4ce1b6 100%)',
  mint_premium_iv: 'linear-gradient(180deg, #ffffff 40%, #4ce1b6 40%, #8ef0d4 100%)',
};

function AssetUploadCard({
  title,
  hint,
  icon: Icon,
  previewUrl,
  onSelect,
  onRemove,
  isDark,
  neutralBtnSx,
  accept = 'image/png,image/jpeg,image/webp',
}) {
  const inputId = `invoice-asset-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: `1px dashed ${isDark ? alpha('#fff', 0.16) : alpha('#0f172a', 0.12)}`,
        bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#0f172a', 0.015),
        height: '100%',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
            {hint}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          height: 72,
          borderRadius: 1.5,
          bgcolor: isDark ? '#0b1220' : '#fff',
          border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {previewUrl ? (
          <>
            <Box component="img" src={previewUrl} alt={title} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', p: 0.75 }} />
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: alpha('#000', 0.55),
                color: '#fff',
                width: 24,
                height: 24,
                '&:hover': { bgcolor: alpha('#000', 0.72) },
              }}
              aria-label={`Remove ${title}`}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            PNG or JPG
          </Typography>
        )}
      </Box>
      <Button
        component="label"
        htmlFor={inputId}
        variant="outlined"
        size="small"
        fullWidth
        sx={{ mt: 1, ...neutralBtnSx }}
      >
        {previewUrl ? 'Replace' : 'Upload'}
        <input id={inputId} type="file" accept={accept} hidden onChange={(e) => onSelect(e.target.files?.[0] || null)} />
      </Button>
    </Box>
  );
}

function TemplateCard({
  template,
  selected,
  onSelect,
  onPreview,
  onClone,
  onDelete,
  isDark,
  neutralBtnSx,
  vertical = false,
}) {
  const swatch = LAYOUT_SWATCH[template.layout_style] || LAYOUT_SWATCH.classic;
  const selectedBorder = isDark ? alpha('#ffffff', 0.42) : alpha('#0f172a', 0.32);
  return (
    <Paper
      elevation={0}
      onClick={() => onSelect(template.id)}
      sx={{
        flex: vertical ? '0 0 auto' : '0 0 200px',
        width: vertical ? '100%' : 200,
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: 2,
        border: `1.5px solid ${selected ? selectedBorder : isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
        boxShadow: selected
          ? isDark ? '0 4px 16px rgba(0,0,0,0.35)' : '0 4px 14px rgba(15,23,42,0.1)'
          : 'none',
        transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          borderColor: selected ? selectedBorder : isDark ? alpha('#fff', 0.2) : alpha('#0f172a', 0.18),
        },
      }}
    >
      <Box sx={{ height: 72, background: swatch, p: 1.25, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 10,
            borderRadius: 1,
            bgcolor: alpha('#fff', 0.92),
            boxShadow: '0 4px 12px rgba(15,23,42,0.1)',
            p: 0.75,
          }}
        >
          <Box sx={{ height: 6, width: '42%', bgcolor: alpha(template.accent_color || '#0d9488', 0.85), borderRadius: 0.5, mb: 0.5 }} />
          <Box sx={{ height: 4, width: '68%', bgcolor: alpha('#0f172a', 0.1), borderRadius: 0.5, mb: 0.35 }} />
          <Box sx={{ height: 4, width: '50%', bgcolor: alpha('#0f172a', 0.07), borderRadius: 0.5 }} />
        </Box>
        {selected ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            label="Active"
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              height: 22,
              bgcolor: ACCENT,
              color: '#04120e',
              fontWeight: 700,
              fontSize: '0.6875rem',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ) : template.is_default_template ? (
          <Chip
            label="Default"
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              height: 22,
              bgcolor: alpha('#0f172a', 0.72),
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.6875rem',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ p: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.25 }} noWrap>
          {template.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }} noWrap>
          {template.layout_style} · {template.is_system ? 'System' : 'Custom'}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PreviewIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => onPreview(template)}
            sx={{ flex: 1, minWidth: 0, py: 0.35, fontSize: '0.75rem', ...neutralBtnSx }}
          >
            Preview
          </Button>
          {template.is_system ? (
            <IconButton size="small" onClick={() => onClone(template)} aria-label="Clone template" sx={{ border: `1px solid ${isDark ? alpha('#fff', 0.14) : alpha('#0f172a', 0.14)}`, borderRadius: 1.5, width: 30, height: 30 }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : (
            <IconButton size="small" color="error" onClick={() => onDelete(template)} aria-label="Delete template" sx={{ width: 30, height: 30 }}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

export default function InvoiceSetupPage({ showHeader = true }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = isConfigurationMobileLayout();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [footerNote, setFooterNote] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [tagline, setTagline] = useState('');
  const [authorizedSignatoryName, setAuthorizedSignatoryName] = useState('');
  const [bottomBarMessage, setBottomBarMessage] = useState('');
  const [invoiceTerms, setInvoiceTerms] = useState('');
  const [hsnSacCode, setHsnSacCode] = useState('996311');
  const [restaurantHsnSacCode, setRestaurantHsnSacCode] = useState('996331');
  const [hallHsnSacCode, setHallHsnSacCode] = useState('997212');
  const [showItemRoomNumber, setShowItemRoomNumber] = useState(true);
  const [showItemRoomType, setShowItemRoomType] = useState(true);
  const [showItemRatePlan, setShowItemRatePlan] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIfscCode, setBankIfscCode] = useState('');
  const [bankAccountHolder, setBankAccountHolder] = useState('');
  const [usePropertyLogo, setUsePropertyLogo] = useState(true);
  const [invoiceLogoUrl, setInvoiceLogoUrl] = useState('');
  const [digitalSignatureUrl, setDigitalSignatureUrl] = useState('');
  const [invoiceLogoPreview, setInvoiceLogoPreview] = useState('');
  const [signaturePreview, setSignaturePreview] = useState('');
  const [invoiceLogoFile, setInvoiceLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [removeInvoiceLogo, setRemoveInvoiceLogo] = useState(false);
  const [removeDigitalSignature, setRemoveDigitalSignature] = useState(false);
  const [invoiceNumberPrefix, setInvoiceNumberPrefix] = useState('');
  const [invoiceNumberStart, setInvoiceNumberStart] = useState('');
  const [error, setError] = useState('');
  const { pageToasts, dismissPageToast, showSuccess, showError } = usePageToast();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cloneDialog, setCloneDialog] = useState({ open: false, source: null, name: '' });
  const loadSeqRef = useRef(0);
  const savingRef = useRef(false);
  const latestFieldsRef = useRef({});

  const cardSx = getConfigurationCompactCardSx(isDark, isMobile);
  const fieldSx = getConfigurationTextFieldSx(isDark);
  const sectionTitleSx = getConfigurationCompactSectionTitleSx(isDark);
  const neutralBtnSx = getConfigurationNeutralOutlinedButtonSx(isDark);

  // Keep a render-synced snapshot so Save always sends the latest typed HSN/copy,
  // even if a stale load effect races with the click handler.
  latestFieldsRef.current = {
    activeTemplateId,
    footerNote,
    serviceDescription,
    tagline,
    authorizedSignatoryName,
    bottomBarMessage,
    invoiceTerms,
    hsnSacCode,
    restaurantHsnSacCode,
    hallHsnSacCode,
    showItemRoomNumber,
    showItemRoomType,
    showItemRatePlan,
    bankName,
    bankAccountNumber,
    bankIfscCode,
    bankAccountHolder,
    usePropertyLogo,
    removeInvoiceLogo,
    removeDigitalSignature,
    invoiceNumberPrefix,
    invoiceNumberStart,
    templates,
  };

  const applySettingsPayload = useCallback((data, { resetUploads = false } = {}) => {
    const settings = data?.settings || {};
    const nextTemplates = Array.isArray(data?.templates) ? data.templates : [];
    setTemplates(nextTemplates);
    setActiveTemplateId(
      settings.active_template_id
      || nextTemplates.find((t) => t.is_active)?.id
      || nextTemplates[0]?.id
      || null,
    );
    setFooterNote(settings.footer_note || '');
    setServiceDescription(settings.service_description || '');
    setTagline(settings.tagline || '');
    setAuthorizedSignatoryName(settings.authorized_signatory_name || '');
    setBottomBarMessage(settings.bottom_bar_message || '');
    setInvoiceTerms(settings.invoice_terms || '');
    setHsnSacCode(normalizeHsn(settings.hsn_sac_code, ''));
    setRestaurantHsnSacCode(normalizeHsn(settings.restaurant_hsn_sac_code, ''));
    setHallHsnSacCode(normalizeHsn(settings.hall_hsn_sac_code, ''));
    setShowItemRoomNumber(settings.show_item_room_number !== false);
    setShowItemRoomType(settings.show_item_room_type !== false);
    setShowItemRatePlan(settings.show_item_rate_plan === true);
    setBankName(settings.bank_name || '');
    setBankAccountNumber(settings.bank_account_number || '');
    setBankIfscCode(settings.bank_ifsc_code || '');
    setBankAccountHolder(settings.bank_account_holder || '');
    setUsePropertyLogo(settings.use_property_logo !== false);
    setInvoiceNumberPrefix(settings.invoice_number_prefix || '');
    setInvoiceNumberStart(settings.invoice_number_start || '');
    setInvoiceLogoUrl(settings.invoice_logo_url || '');
    setDigitalSignatureUrl(settings.digital_signature_url || '');
    if (resetUploads) {
      setInvoiceLogoPreview((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
      setSignaturePreview((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
      setInvoiceLogoFile(null);
      setSignatureFile(null);
      setRemoveInvoiceLogo(false);
      setRemoveDigitalSignature(false);
    }
  }, []);

  const loadSetup = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const data = await getInvoiceSetup();
      // Ignore stale responses from StrictMode / remount races so typed HSN is not wiped.
      if (seq !== loadSeqRef.current || savingRef.current) return;
      applySettingsPayload(data, { resetUploads: true });
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setError(e?.response?.data?.error || e?.message || 'Failed to load invoice setup');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [applySettingsPayload]);

  useEffect(() => {
    loadSetup();
    return () => {
      loadSeqRef.current += 1;
    };
  }, [loadSetup]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (invoiceLogoPreview?.startsWith('blob:')) URL.revokeObjectURL(invoiceLogoPreview);
      if (signaturePreview?.startsWith('blob:')) URL.revokeObjectURL(signaturePreview);
    },
    [previewUrl, invoiceLogoPreview, signaturePreview],
  );

  const handleLogoSelect = (file) => {
    if (invoiceLogoPreview?.startsWith('blob:')) URL.revokeObjectURL(invoiceLogoPreview);
    if (!file) return;
    setInvoiceLogoFile(file);
    setRemoveInvoiceLogo(false);
    setInvoiceLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoRemove = () => {
    if (invoiceLogoPreview?.startsWith('blob:')) URL.revokeObjectURL(invoiceLogoPreview);
    setInvoiceLogoFile(null);
    setInvoiceLogoPreview('');
    setRemoveInvoiceLogo(Boolean(invoiceLogoUrl));
  };

  const handleSignatureSelect = (file) => {
    if (signaturePreview?.startsWith('blob:')) URL.revokeObjectURL(signaturePreview);
    if (!file) return;
    setSignatureFile(file);
    setRemoveDigitalSignature(false);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const handleSignatureRemove = () => {
    if (signaturePreview?.startsWith('blob:')) URL.revokeObjectURL(signaturePreview);
    setSignatureFile(null);
    setSignaturePreview('');
    setRemoveDigitalSignature(Boolean(digitalSignatureUrl));
  };

  const handleSave = async () => {
    const fields = latestFieldsRef.current;
    const templateId =
      fields.activeTemplateId
      || fields.templates.find((t) => t.is_active)?.id
      || fields.templates[0]?.id
      || null;
    if (!templateId) {
      showError('Select an invoice template before saving.');
      return;
    }
    // Blank HSN is intentional — clears code and hides that column on invoices.
    const hotelHsn = normalizeHsn(fields.hsnSacCode, '');
    const restaurantHsn = normalizeHsn(fields.restaurantHsnSacCode, '');
    const hallHsn = normalizeHsn(fields.hallHsnSacCode, '');

    savingRef.current = true;
    loadSeqRef.current += 1; // invalidate in-flight loads
    setSaving(true);
    setError('');
    try {
      const payload = {
        active_template_id: templateId,
        footer_note: fields.footerNote,
        service_description: fields.serviceDescription,
        tagline: fields.tagline,
        authorized_signatory_name: fields.authorizedSignatoryName,
        bottom_bar_message: fields.bottomBarMessage,
        invoice_terms: fields.invoiceTerms,
        hsn_sac_code: hotelHsn,
        restaurant_hsn_sac_code: restaurantHsn,
        hall_hsn_sac_code: hallHsn,
        show_item_room_number: fields.showItemRoomNumber,
        show_item_room_type: fields.showItemRoomType,
        show_item_rate_plan: fields.showItemRatePlan,
        bank_name: fields.bankName,
        bank_account_number: fields.bankAccountNumber,
        bank_ifsc_code: fields.bankIfscCode,
        bank_account_holder: fields.bankAccountHolder,
        use_property_logo: fields.usePropertyLogo,
        invoice_number_prefix: fields.invoiceNumberPrefix,
        invoice_number_start: normalizeInvoiceStart(fields.invoiceNumberStart),
        remove_invoice_logo: fields.removeInvoiceLogo,
        remove_digital_signature: fields.removeDigitalSignature,
      };
      const data = await updateInvoiceSetup(payload, {
        invoice_logo: invoiceLogoFile,
        digital_signature: signatureFile,
      });
      if (!data?.settings) {
        throw new Error('Save succeeded but no settings were returned. Please refresh and try again.');
      }
      const savedHotelHsn = normalizeHsn(data.settings.hsn_sac_code, '');
      const savedRestaurantHsn = normalizeHsn(data.settings.restaurant_hsn_sac_code, '');
      const savedHallHsn = normalizeHsn(data.settings.hall_hsn_sac_code, '');
      if (
        savedHotelHsn !== hotelHsn
        || savedRestaurantHsn !== restaurantHsn
        || savedHallHsn !== hallHsn
      ) {
        throw new Error(
          `HSN/SAC codes did not persist (saved hotel=${savedHotelHsn || '—'}, restaurant=${savedRestaurantHsn || '—'}, hall=${savedHallHsn || '—'}). Please try again.`,
        );
      }
      applySettingsPayload(data, { resetUploads: true });
      showSuccess('Invoice setup saved successfully.');
    } catch (e) {
      const message = e?.response?.data?.error || e?.message || 'Failed to save';
      showError(message);
      setError(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handlePreview = async (template) => {
    setPreviewOpen(true);
    setPreviewTitle(template.name);
    setPreviewLoading(true);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    try {
      const blob = await fetchInvoiceTemplatePreviewBlob(template.id);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClone = async () => {
    if (!cloneDialog.source || !cloneDialog.name.trim()) return;
    try {
      const data = await createInvoiceTemplate({
        source_template_id: cloneDialog.source.id,
        name: cloneDialog.name.trim(),
      });
      setTemplates(data.templates || []);
      setActiveTemplateId(data.settings?.active_template_id || null);
      setCloneDialog({ open: false, source: null, name: '' });
      showSuccess('Custom template created successfully.');
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || 'Failed to clone template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      const data = await deleteInvoiceTemplate(template.id);
      setTemplates(data.templates || []);
      setActiveTemplateId(data.settings?.active_template_id || null);
      showSuccess('Template deleted successfully.');
    } catch (e) {
      showError(e?.response?.data?.error || e?.message || 'Failed to delete template');
    }
  };

  const openCloneDialog = (source) => setCloneDialog({ open: true, source, name: `${source.name} Copy` });

  const saveButtonSx = {
    bgcolor: ACCENT,
    color: '#04120e',
    fontWeight: 800,
    fontSize: '0.9375rem',
    borderRadius: 1.75,
    px: 3,
    py: 1.1,
    minHeight: 42,
    boxShadow: 'none',
    '& .MuiButton-startIcon': { mr: 1 },
    '&:hover': { bgcolor: ACCENT, opacity: 0.92, boxShadow: 'none' },
  };

  return (
    <Box>
      {showHeader ? (
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(ACCENT, 0.12),
              color: ACCENT,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Invoice Setup
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Template, branding, and fixed invoice fields
            </Typography>
          </Box>
        </Stack>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={28} sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} md={7} lg={8}>
            <Paper elevation={0} sx={cardSx}>
              <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>Branding &amp; defaults</Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <AssetUploadCard
                    title="Invoice logo"
                    hint="Header logo; falls back to property logo"
                    icon={ImageIcon}
                    previewUrl={invoiceLogoPreview || (!removeInvoiceLogo ? invoiceLogoUrl : '')}
                    onSelect={handleLogoSelect}
                    onRemove={handleLogoRemove}
                    isDark={isDark}
                    neutralBtnSx={neutralBtnSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AssetUploadCard
                    title="Digital signature"
                    hint="Signatory block on detailed layouts"
                    icon={DrawIcon}
                    previewUrl={signaturePreview || (!removeDigitalSignature ? digitalSignatureUrl : '')}
                    onSelect={handleSignatureSelect}
                    onRemove={handleSignatureRemove}
                    isDark={isDark}
                    neutralBtnSx={neutralBtnSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={usePropertyLogo}
                        onChange={(e) => setUsePropertyLogo(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        Use property logo when no invoice logo is uploaded
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.75, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

              <Typography sx={sectionTitleSx}>Invoice number sequence</Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.25,
                  fontSize: '0.75rem',
                  color: isDark ? alpha('#fff', 0.55) : alpha('#0f172a', 0.55),
                }}
              >
                Set a starting number to use a custom sequence. Leave start empty to keep the default format (INV-14-2026-000024).
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Prefix (optional)"
                    value={invoiceNumberPrefix}
                    onChange={(e) => setInvoiceNumberPrefix(e.target.value)}
                    placeholder="INV-"
                    helperText="Leave empty for numbers only"
                    inputProps={{ maxLength: 40 }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Starting number"
                    value={invoiceNumberStart}
                    onChange={(e) => setInvoiceNumberStart(normalizeInvoiceStart(e.target.value))}
                    placeholder="1000 or 00001"
                    helperText="Digit length is kept (00001 → 00002)"
                    inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? alpha('#fff', 0.65) : alpha('#0f172a', 0.6) }}>
                    {invoiceNumberStart
                      ? `Next invoices: ${previewInvoiceNumber(invoiceNumberPrefix, invoiceNumberStart) || '—'}, then increment.`
                      : 'Default format stays active until a starting number is saved.'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.75, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

              <Typography sx={sectionTitleSx}>Invoice copy</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Header tagline"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Premium Stay · Gonda, Uttar Pradesh"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Authorized signatory"
                    value={authorizedSignatoryName}
                    onChange={(e) => setAuthorizedSignatoryName(e.target.value)}
                    placeholder="Manager name"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Service description"
                    value={serviceDescription}
                    onChange={(e) => setServiceDescription(e.target.value)}
                    placeholder="Accommodation / Lodging"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hotel HSN/SAC (room stay)"
                    value={hsnSacCode}
                    onChange={(e) => setHsnSacCode(e.target.value)}
                    placeholder="996311"
                    helperText="Leave blank to hide HSN/SAC column on room invoices"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Restaurant HSN/SAC (food)"
                    value={restaurantHsnSacCode}
                    onChange={(e) => setRestaurantHsnSacCode(e.target.value)}
                    placeholder="996331"
                    helperText="Leave blank to hide HSN/SAC column on restaurant invoices"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hall HSN/SAC (banquet)"
                    value={hallHsnSacCode}
                    onChange={(e) => setHallHsnSacCode(e.target.value)}
                    placeholder="997212"
                    helperText="Leave blank to hide HSN/SAC column on hall invoices"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>Invoice item details</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: '0.75rem',
                      color: isDark ? alpha('#fff', 0.55) : alpha('#0f172a', 0.55),
                    }}
                  >
                    Choose what appears in each room line on the invoice (e.g. Room No.106 (DELUXE ROOM)).
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} flexWrap="wrap">
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={showItemRoomNumber}
                          onChange={(e) => setShowItemRoomNumber(e.target.checked)}
                          sx={{
                            color: ACCENT,
                            '&.Mui-checked': { color: ACCENT },
                          }}
                        />
                      )}
                      label={<Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>Room No.</Typography>}
                    />
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={showItemRoomType}
                          onChange={(e) => setShowItemRoomType(e.target.checked)}
                          sx={{
                            color: ACCENT,
                            '&.Mui-checked': { color: ACCENT },
                          }}
                        />
                      )}
                      label={<Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>Room type</Typography>}
                    />
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={showItemRatePlan}
                          onChange={(e) => setShowItemRatePlan(e.target.checked)}
                          sx={{
                            color: ACCENT,
                            '&.Mui-checked': { color: ACCENT },
                          }}
                        />
                      )}
                      label={<Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>Rate plan</Typography>}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Footer note"
                    value={footerNote}
                    onChange={(e) => setFooterNote(e.target.value)}
                    multiline
                    minRows={2}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bottom bar message"
                    value={bottomBarMessage}
                    onChange={(e) => setBottomBarMessage(e.target.value)}
                    multiline
                    minRows={2}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Terms & conditions"
                    value={invoiceTerms}
                    onChange={(e) => setInvoiceTerms(e.target.value)}
                    placeholder="One term per line"
                    multiline
                    minRows={2}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.75, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

              <Typography sx={sectionTitleSx}>Bank details</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Bank name" value={bankName} onChange={(e) => setBankName(e.target.value)} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Account holder" value={bankAccountHolder} onChange={(e) => setBankAccountHolder(e.target.value)} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Account number" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="IFSC code" value={bankIfscCode} onChange={(e) => setBankIfscCode(e.target.value)} sx={fieldSx} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.75, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="button"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: '20px !important' }} />}
                  disabled={saving || !(activeTemplateId || templates.length)}
                  onClick={handleSave}
                  sx={{
                    ...saveButtonSx,
                    alignSelf: { xs: 'stretch', sm: 'flex-end' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5} lg={4}>
            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                position: { md: 'sticky' },
                top: { md: 16 },
                maxHeight: { md: 'calc(100vh - 120px)' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography sx={sectionTitleSx}>Templates</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.75, mb: 1.25 }}>
                Select the design used for tax invoices
              </Typography>
              <Stack
                spacing={1.25}
                sx={{
                  overflowY: { md: 'auto' },
                  flex: 1,
                  pr: { md: 0.5 },
                  scrollbarWidth: 'thin',
                }}
              >
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={template.id === activeTemplateId}
                    onSelect={setActiveTemplateId}
                    onPreview={handlePreview}
                    onClone={openCloneDialog}
                    onDelete={handleDelete}
                    isDark={isDark}
                    neutralBtnSx={neutralBtnSx}
                    vertical
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, py: 1.5 }}>{previewTitle} — Preview</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: isDark ? '#0b0f17' : '#e8edf3', p: 0 }}>
          {previewLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360, gap: 1 }}>
              <CircularProgress size={28} sx={{ color: ACCENT }} />
              <Typography variant="body2" color="text.secondary">Generating sample invoice…</Typography>
            </Stack>
          ) : previewUrl ? (
            <Box sx={{ p: 1 }}>
              <PdfFitPreview pdfUrl={previewUrl} accentColor={ACCENT} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setPreviewOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneDialog.open} onClose={() => setCloneDialog({ open: false, source: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, py: 1.5 }}>Clone template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Custom template name"
            value={cloneDialog.name}
            onChange={(e) => setCloneDialog((prev) => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 0.5, ...fieldSx }}
          />
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setCloneDialog({ open: false, source: null, name: '' })} size="small">Cancel</Button>
          <Button variant="contained" onClick={handleClone} size="small" sx={{ bgcolor: ACCENT, color: '#04120e', boxShadow: 'none' }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <PageToastStack toasts={pageToasts} onDismiss={dismissPageToast} />
    </Box>
  );
}
